// ─────────────────────────────────────────────────────────────────────────────
// StudentExams.jsx
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import portalService from "../../../services/portalService";
import { useApp } from "../../../context/AppContext";
const extract = (r) => r?.data ?? r;

const TYPE_COLORS = {
    Internal: { bg: "#e8f1f8", color: "#002747" },
    External: { bg: "#ede9fe", color: "#7c3aed" },
    Practical: { bg: "#dcfce7", color: "#15803d" },
    Sessional: { bg: "#fef9c3", color: "#92400e" },
    University: { bg: "#fee2e2", color: "#dc2626" },
    Other: { bg: "#f3f4f6", color: "#6b7280" },
};

export function StudentExams() {
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        portalService
            .getExams()
            .then((r) => setExams(extract(r) || []))
            .catch((err) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading)
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading exams…
            </div>
        );

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">My Exams</h1>
                    <p className="page-subtitle">
                        {exams.length} scheduled exam{exams.length !== 1 ? "s" : ""}
                    </p>
                </div>
            </div>
            {exams.length === 0 ? (
                <div className="card">
                    <div
                        className="card-body"
                        style={{
                            textAlign: "center",
                            color: "var(--text-muted)",
                            padding: "2rem",
                        }}
                    >
                        No exams scheduled for your class yet.
                    </div>
                </div>
            ) : (
                <div style={{ display: "grid", gap: "0.75rem" }}>
                    {exams.map((exam) => {
                        const tc = TYPE_COLORS[exam.examType] || TYPE_COLORS.Other;
                        return (
                            <div
                                key={exam._id}
                                className="card"
                                style={{
                                    padding: "1rem 1.25rem",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "1rem",
                                    borderLeft: `4px solid ${tc.color}`,
                                }}
                            >
                                <div
                                    style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 10,
                                        background: tc.bg,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    <span
                                        style={{ fontSize: 10, fontWeight: 800, color: tc.color }}
                                    >
                                        {exam.examType.slice(0, 3).toUpperCase()}
                                    </span>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14 }}>
                                        {exam.title}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 12.5,
                                            color: "var(--text-muted)",
                                            marginTop: 2,
                                        }}
                                    >
                                        {exam.examDate ? `📅 ${exam.examDate}` : "Date TBD"}
                                        <span style={{ margin: "0 6px" }}>·</span>
                                        {exam.subjects?.length ?? 0} subject
                                        {exam.subjects?.length !== 1 ? "s" : ""}
                                    </div>
                                    {exam.subjects?.length > 0 && (
                                        <div
                                            style={{
                                                marginTop: "0.5rem",
                                                display: "flex",
                                                gap: "0.4rem",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {exam.subjects.map((s) => (
                                                <span
                                                    key={s.name}
                                                    style={{
                                                        fontSize: 11.5,
                                                        background: "#f8fafc",
                                                        border: "1px solid var(--border)",
                                                        padding: "2px 8px",
                                                        borderRadius: 99,
                                                    }}
                                                >
                                                    {s.name}{" "}
                                                    <span style={{ color: "var(--text-muted)" }}>
                                                        /{s.maxMarks}
                                                    </span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <span
                                    style={{
                                        ...tc,
                                        padding: "3px 9px",
                                        borderRadius: 99,
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        flexShrink: 0,
                                    }}
                                >
                                    {exam.status}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// StudentTimetable.jsx
// ─────────────────────────────────────────────────────────────────────────────
const DAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];
const PERIOD_COLORS = {
    Lecture: { bg: "#e8f1f8", color: "#002747" },
    Practical: { bg: "#dcfce7", color: "#15803d" },
    Tutorial: { bg: "#fef9c3", color: "#92400e" },
    Break: { bg: "#f3f4f6", color: "#6b7280" },
    Free: { bg: "#ffffff", color: "#d1d5db" },
};

export function StudentTimetable() {
    const [timetable, setTimetable] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeDay, setActiveDay] = useState(
        DAYS[new Date().getDay() === 0 ? 0 : new Date().getDay() - 1] || "Monday",
    );

    useEffect(() => {
        portalService
            .getTimetable()
            .then((r) => setTimetable(extract(r)))
            .catch(() => setTimetable(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading)
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading timetable…
            </div>
        );

    if (!timetable)
        return (
            <div>
                <div className="page-header">
                    <h1 className="page-title">Timetable</h1>
                </div>
                <div className="card">
                    <div
                        className="card-body"
                        style={{
                            textAlign: "center",
                            color: "var(--text-muted)",
                            padding: "2rem",
                        }}
                    >
                        No timetable set for your class yet.
                    </div>
                </div>
            </div>
        );

    const scheduleMap = Object.fromEntries(
        timetable.schedule.map((d) => [d.day, d.periods]),
    );
    const activePeriods = scheduleMap[activeDay] || [];

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Timetable</h1>
                    <p className="page-subtitle">
                        {timetable.courseAdmitted} · {timetable.courseYear}
                    </p>
                </div>
            </div>

            <div className="card">
                {/* Day tabs */}
                <div
                    style={{
                        display: "flex",
                        borderBottom: "1px solid var(--border)",
                        overflowX: "auto",
                    }}
                >
                    {DAYS.map((day) => {
                        const isActive = activeDay === day;
                        const filled = (scheduleMap[day] || []).filter(
                            (p) => p.subject,
                        ).length;
                        return (
                            <button
                                key={day}
                                type="button"
                                onClick={() => setActiveDay(day)}
                                style={{
                                    padding: "0.65rem 1rem",
                                    border: "none",
                                    borderBottom: isActive
                                        ? "3px solid #003b67"
                                        : "3px solid transparent",
                                    background: "transparent",
                                    fontSize: 13,
                                    fontWeight: isActive ? 700 : 400,
                                    color: isActive ? "#003b67" : "var(--text-muted)",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {day.slice(0, 3)}
                                {filled > 0 && (
                                    <span
                                        style={{
                                            marginLeft: 4,
                                            fontSize: 10,
                                            background: "#003b67",
                                            color: "#fff",
                                            borderRadius: 99,
                                            padding: "1px 5px",
                                        }}
                                    >
                                        {filled}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Periods */}
                <div style={{ padding: "1rem" }}>
                    {timetable.periodSlots.map((slot, i) => {
                        const period = activePeriods[i] || {};
                        const isEmpty = !period.subject;
                        const pc = PERIOD_COLORS[period.type] || PERIOD_COLORS.Free;
                        return (
                            <div
                                key={slot.periodNo}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "100px 1fr",
                                    gap: "0.75rem",
                                    alignItems: "center",
                                    padding: "0.6rem 0.75rem",
                                    marginBottom: "0.4rem",
                                    borderRadius: 8,
                                    background: isEmpty ? "#f8fafc" : pc.bg,
                                    border: `1px solid ${isEmpty ? "var(--border)" : pc.color + "30"}`,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: "var(--text-muted)",
                                    }}
                                >
                                    <div>
                                        {slot.startTime} – {slot.endTime}
                                    </div>
                                    {slot.label && (
                                        <div
                                            style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 1 }}
                                        >
                                            {slot.label}
                                        </div>
                                    )}
                                </div>
                                {isEmpty ? (
                                    <div
                                        style={{
                                            fontSize: 12.5,
                                            color: "#c4c4c4",
                                            fontStyle: "italic",
                                        }}
                                    >
                                        Free period
                                    </div>
                                ) : (
                                    <div>
                                        <div
                                            style={{
                                                fontWeight: 700,
                                                fontSize: 13.5,
                                                color: pc.color,
                                            }}
                                        >
                                            {period.subject}
                                        </div>
                                        <div
                                            style={{ fontSize: 12, color: pc.color, opacity: 0.8 }}
                                        >
                                            {period.teacher && <span>{period.teacher}</span>}
                                            {period.teacher && period.room && (
                                                <span style={{ margin: "0 5px" }}>·</span>
                                            )}
                                            {period.room && <span>📍 {period.room}</span>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// StudentFees.jsx
// ─────────────────────────────────────────────────────────────────────────────
const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

export function StudentFees() {
    const [fees, setFees] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        portalService
            .getFees()
            .then((r) => setFees(extract(r)))
            .catch((err) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading)
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading fees…
            </div>
        );

    const pct =
        fees?.feesDecided > 0
            ? Math.round((fees.totalPaid / fees.feesDecided) * 100)
            : 0;

    return (
        <div>
            <div className="page-header">
                <h1 className="page-title">My Fees</h1>
            </div>
            {fees && (
                <>
                    {/* Summary */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                            gap: "1rem",
                            marginBottom: "1.25rem",
                        }}
                    >
                        {[
                            {
                                label: "Total Fees",
                                value: inr(fees.feesDecided),
                                color: "#003b67",
                            },
                            { label: "Paid", value: inr(fees.totalPaid), color: "#16a34a" },
                            {
                                label: "Scholarship",
                                value: inr(fees.scholarshipAmountBalance),
                                color: "#7c3aed",
                            },
                            { label: "Pending", value: inr(fees.pending), color: "#dc2626" },
                        ].map(({ label, value, color }) => (
                            <div
                                key={label}
                                className="card"
                                style={{ padding: "1rem", borderLeft: `4px solid ${color}` }}
                            >
                                <div style={{ fontSize: 20, fontWeight: 800, color }}>
                                    {value}
                                </div>
                                <div
                                    style={{
                                        fontSize: 12.5,
                                        color: "var(--text-muted)",
                                        marginTop: 3,
                                    }}
                                >
                                    {label}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Progress */}
                    <div className="card" style={{ marginBottom: "1rem" }}>
                        <div className="card-header">Payment Progress</div>
                        <div className="card-body">
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: 13,
                                    marginBottom: 6,
                                }}
                            >
                                <span style={{ color: "#16a34a" }}>
                                    Paid: <strong>{inr(fees.totalPaid)}</strong>
                                </span>
                                <span style={{ fontWeight: 700 }}>{pct}%</span>
                                <span style={{ color: "#dc2626" }}>
                                    Pending: <strong>{inr(fees.pending)}</strong>
                                </span>
                            </div>
                            <div
                                style={{
                                    height: 10,
                                    borderRadius: 99,
                                    background: "#fee2e2",
                                    overflow: "hidden",
                                }}
                            >
                                <div
                                    style={{
                                        height: "100%",
                                        width: `${pct}%`,
                                        background: "linear-gradient(90deg,#16a34a,#22d3ee)",
                                        borderRadius: 99,
                                        transition: "width 0.7s",
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Receipts */}
                    {fees.receipts?.length > 0 && (
                        <div className="card">
                            <div className="card-header">Payment History</div>
                            <div className="table-wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Receipt No.</th>
                                            <th>Installment</th>
                                            <th style={{ textAlign: "right" }}>Amount</th>
                                            <th>Mode</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fees.receipts.map((r) => (
                                            <tr key={r._id}>
                                                <td>
                                                    <span className="badge badge-blue">
                                                        {r.receiptNo}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: 13 }}>
                                                    {r.installmentLabel || r.installmentNo}
                                                </td>
                                                <td
                                                    style={{
                                                        textAlign: "right",
                                                        fontWeight: 700,
                                                        color: "#16a34a",
                                                    }}
                                                >
                                                    {inr(r.amountPaid)}
                                                </td>
                                                <td style={{ fontSize: 13 }}>{r.paymentMode}</td>
                                                <td
                                                    style={{ fontSize: 12.5, color: "var(--text-muted)" }}
                                                >
                                                    {r.paymentDate}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// StudentCertificates.jsx
// ─────────────────────────────────────────────────────────────────────────────
export function StudentCertificates() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        portalService
            .getCertificates()
            .then((r) => {
                const cert = r?.data ?? r;   // handles both { success, data: X } and X directly
                setData(cert);
            })
            .catch((err) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading)
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading certificates…
            </div>
        );

    const total = (data?.bonafide?.length ?? 0) + (data?.leaving?.length ?? 0);

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">My Certificates</h1>
                    <p className="page-subtitle">
                        {total} certificate{total !== 1 ? "s" : ""} issued
                    </p>
                </div>
            </div>

            {total === 0 ? (
                <div className="card">
                    <div
                        className="card-body"
                        style={{
                            textAlign: "center",
                            color: "var(--text-muted)",
                            padding: "2rem",
                        }}
                    >
                        No certificates issued yet.
                    </div>
                </div>
            ) : (
                <div style={{ display: "grid", gap: "1rem" }}>
                    {data.bonafide?.length > 0 && (
                        <div className="card">
                            <div className="card-header">Bonafide Certificates</div>
                            <div className="table-wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Sr. No.</th>
                                            <th>Class</th>
                                            <th>Session</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.bonafide.map((b) => (
                                            <tr key={b._id}>
                                                <td>
                                                    <span className="badge badge-blue">{b.srNo}</span>
                                                </td>
                                                <td style={{ fontSize: 13 }}>{b.className}</td>
                                                <td style={{ fontSize: 13 }}>
                                                    {b.sessionStart} – {b.sessionEnd}
                                                </td>
                                                <td
                                                    style={{ fontSize: 12.5, color: "var(--text-muted)" }}
                                                >
                                                    {b.certificateDate}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {data.leaving?.length > 0 && (
                        <div className="card">
                            <div className="card-header">Leaving Certificates (TC)</div>
                            <div className="table-wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Serial No.</th>
                                            <th>Standard</th>
                                            <th>Date of Leaving</th>
                                            <th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.leaving.map((l) => (
                                            <tr key={l._id}>
                                                <td>
                                                    <span className="badge badge-blue">{l.serialNo}</span>
                                                </td>
                                                <td style={{ fontSize: 13 }}>{l.standard}</td>
                                                <td
                                                    style={{ fontSize: 12.5, color: "var(--text-muted)" }}
                                                >
                                                    {l.dateOfLeaving}
                                                </td>
                                                <td style={{ fontSize: 13 }}>{l.reasonOfLeaving}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// StudentEvents.jsx
// ─────────────────────────────────────────────────────────────────────────────
const EVENT_CONFIG = {
    Holiday: { color: "#dc2626", bg: "#fee2e2" },
    Exam: { color: "#7c3aed", bg: "#ede9fe" },
    Event: { color: "#003b67", bg: "#e8f1f8" },
    Meeting: { color: "#d97706", bg: "#fef3c7" },
    Other: { color: "#6b7280", bg: "#f3f4f6" },
};

const MONTHS_LONG = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
const nowE = new Date();

export function StudentEvents() {
    const [events, setEvents] = useState([]);
    const [month, setMonth] = useState(nowE.getMonth() + 1);
    const [year, setYear] = useState(nowE.getFullYear());
    const [loading, setLoading] = useState(true);
    const yearOpts = Array.from({ length: 3 }, (_, i) => nowE.getFullYear() - i);

    useEffect(() => {
        setLoading(true);
        portalService
            .getEvents({ month, year })
            .then((r) => setEvents(extract(r) || []))
            .catch((err) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, [month, year]);

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">Events & Calendar</h1>
                    <p className="page-subtitle">
                        {events.length} event{events.length !== 1 ? "s" : ""} in{" "}
                        {MONTHS_LONG[month]}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <select
                        className="form-control"
                        style={{ width: 130, fontSize: 13 }}
                        value={month}
                        onChange={(e) => setMonth(Number(e.target.value))}
                    >
                        {MONTHS_LONG.slice(1).map((m, i) => (
                            <option key={i + 1} value={i + 1}>
                                {m}
                            </option>
                        ))}
                    </select>
                    <select
                        className="form-control"
                        style={{ width: 100, fontSize: 13 }}
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                    >
                        {yearOpts.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="loading-center">
                    <div className="spinner" />
                    Loading events…
                </div>
            ) : events.length === 0 ? (
                <div className="card">
                    <div
                        className="card-body"
                        style={{
                            textAlign: "center",
                            color: "var(--text-muted)",
                            padding: "2rem",
                        }}
                    >
                        No events in {MONTHS_LONG[month]} {year}.
                    </div>
                </div>
            ) : (
                <div style={{ display: "grid", gap: "0.65rem" }}>
                    {events.map((ev) => {
                        const ec = EVENT_CONFIG[ev.type] || EVENT_CONFIG.Other;
                        return (
                            <div
                                key={ev._id}
                                className="card"
                                style={{
                                    padding: "0.9rem 1.1rem",
                                    display: "flex",
                                    gap: "0.75rem",
                                    alignItems: "flex-start",
                                    borderLeft: `4px solid ${ec.color}`,
                                }}
                            >
                                <div
                                    style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 9,
                                        background: ec.bg,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    <span style={{ fontSize: 14 }}>
                                        {ev.type === "Holiday"
                                            ? "🏖"
                                            : ev.type === "Exam"
                                                ? "📝"
                                                : ev.type === "Meeting"
                                                    ? "👥"
                                                    : "📅"}
                                    </span>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                                        {ev.title}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: "var(--text-muted)",
                                            marginTop: 2,
                                        }}
                                    >
                                        {ev.date}
                                        {ev.endDate && ev.endDate !== ev.date
                                            ? ` → ${ev.endDate}`
                                            : ""}
                                        <span style={{ margin: "0 6px" }}>·</span>
                                        <span style={{ color: ec.color, fontWeight: 600 }}>
                                            {ev.type}
                                        </span>
                                        {ev.isHoliday && (
                                            <span
                                                style={{
                                                    marginLeft: 6,
                                                    fontSize: 10,
                                                    background: "#fee2e2",
                                                    color: "#dc2626",
                                                    padding: "1px 5px",
                                                    borderRadius: 99,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                Holiday
                                            </span>
                                        )}
                                    </div>
                                    {ev.description && (
                                        <div
                                            style={{
                                                fontSize: 12.5,
                                                color: "var(--text-muted)",
                                                marginTop: 4,
                                            }}
                                        >
                                            {ev.description}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// StudentHomework — list of assigned homework with status
// Shared by both /student/homework and /parent/homework (read-only for parent,
// same pattern as every other page in this file — the backend already scopes
// data via req.user.linkedStudent for both roles).
// ─────────────────────────────────────────────────────────────────────────────
const HW_STATUS_STYLES = {
    Pending: { bg: "#f1f5f9", color: "#475569" },
    Overdue: { bg: "#fee2e2", color: "#dc2626" },
    Submitted: { bg: "#e8f1f8", color: "#002747" },
    Late: { bg: "#fef3c7", color: "#92400e" },
    Graded: { bg: "#dcfce7", color: "#15803d" },
};

export function StudentHomework() {
    const { role } = useApp();
    const navigate = useNavigate();
    const isParent = role === "parent";

    const [homeworkList, setHomeworkList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        portalService
            .getHomework()
            .then((r) => setHomeworkList(extract(r) || []))
            .catch((err) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading)
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading homework…
            </div>
        );

    const filtered =
        filter === "all"
            ? homeworkList
            : filter === "pending"
                ? homeworkList.filter((h) => h.status === "Pending" || h.status === "Overdue")
                : filter === "submitted"
                    ? homeworkList.filter((h) => h.status === "Submitted" || h.status === "Late")
                    : homeworkList.filter((h) => h.status === "Graded");

    const counts = {
        all: homeworkList.length,
        pending: homeworkList.filter((h) => h.status === "Pending" || h.status === "Overdue").length,
        submitted: homeworkList.filter((h) => h.status === "Submitted" || h.status === "Late").length,
        graded: homeworkList.filter((h) => h.status === "Graded").length,
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{isParent ? "Child's Homework" : "My Homework"}</h1>
                    <p className="page-subtitle">
                        {homeworkList.length} assignment{homeworkList.length !== 1 ? "s" : ""}
                    </p>
                </div>
            </div>

            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
                {[
                    ["all", "All"],
                    ["pending", "Pending"],
                    ["submitted", "Submitted"],
                    ["graded", "Graded"],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        className={`btn ${filter === key ? "btn-primary" : "btn-outline"} btn-sm`}
                        onClick={() => setFilter(key)}
                    >
                        {label} ({counts[key]})
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div className="card">
                    <div
                        className="card-body"
                        style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}
                    >
                        Nothing here right now.
                    </div>
                </div>
            ) : (
                <div style={{ display: "grid", gap: "0.75rem" }}>
                    {filtered.map((hw) => {
                        const sc = HW_STATUS_STYLES[hw.status] || HW_STATUS_STYLES.Pending;
                        return (
                            <div
                                key={hw._id}
                                className="card"
                                style={{
                                    padding: "1rem 1.25rem",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "1rem",
                                    borderLeft: `4px solid ${sc.color}`,
                                    cursor: "pointer",
                                }}
                                onClick={() =>
                                    navigate(
                                        isParent
                                            ? `/parent/homework/${hw._id}`
                                            : `/student/homework/${hw._id}`,
                                    )
                                }
                            >
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14 }}>{hw.title}</div>
                                    <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
                                        {hw.subject?.name}
                                        <span style={{ margin: "0 6px" }}>·</span>
                                        Due {hw.dueDate?.slice(0, 10)}
                                        {hw.maxMarks ? (
                                            <>
                                                <span style={{ margin: "0 6px" }}>·</span>
                                                {hw.maxMarks} marks
                                            </>
                                        ) : null}
                                    </div>
                                    {hw.status === "Graded" && hw.submission && (
                                        <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6, color: "#15803d" }}>
                                            {hw.submission.marksObtained}
                                            {hw.maxMarks ? `/${hw.maxMarks}` : ""}
                                        </div>
                                    )}
                                </div>
                                <span
                                    style={{
                                        ...sc,
                                        padding: "3px 9px",
                                        borderRadius: 99,
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        flexShrink: 0,
                                    }}
                                >
                                    {hw.status}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// StudentHomeworkDetail — view assignment + submit (student) or read-only
// view (parent). Shared route pattern: /student/homework/:homeworkId (with
// submit controls) and /parent/homework/:homeworkId (read-only).
// ─────────────────────────────────────────────────────────────────────────────
export function StudentHomeworkDetail() {
    const { homeworkId } = useParams();
    const { role } = useApp();
    const navigate = useNavigate();
    const isParent = role === "parent";

    const [hw, setHw] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submittedText, setSubmittedText] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    // No dedicated single-homework endpoint — re-fetches the list and finds
    // the match client-side, same tradeoff used elsewhere for small lists.
    useEffect(() => {
        portalService
            .getHomework()
            .then((r) => {
                const list = extract(r) || [];
                const found = list.find((h) => h._id === homeworkId);
                if (!found) {
                    toast.error("Homework not found");
                    navigate(isParent ? "/parent/homework" : "/student/homework");
                    return;
                }
                setHw(found);
                if (found.submission) {
                    setSubmittedText(found.submission.submittedText || "");
                    setAttachments(found.submission.attachments || []);
                }
            })
            .catch((err) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, [homeworkId, isParent, navigate]);

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                const r = await portalService.uploadHomeworkFile(file);
                setAttachments((prev) => [...prev, extract(r)]);
            }
        } catch (err) {
            toast.error(err.message);
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const removeAttachment = (i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i));

    const handleSubmit = async () => {
        if (!submittedText.trim() && attachments.length === 0) {
            toast.error("Write an answer, attach a file, or both");
            return;
        }
        setSaving(true);
        try {
            await portalService.submitHomework(homeworkId, { submittedText, attachments });
            toast.success("Submitted successfully");
            navigate("/student/homework");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="loading-center"><div className="spinner" />Loading…</div>;
    if (!hw) return null;

    const isGraded = hw.status === "Graded";
    const isOverdue = hw.status === "Overdue";
    const canEdit = !isParent && !isGraded && (!isOverdue || hw.allowLateSubmission);

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{hw.title}</h1>
                    <p className="page-subtitle">
                        {hw.subject?.name} · Due {hw.dueDate?.slice(0, 10)}
                        {hw.maxMarks ? ` · ${hw.maxMarks} marks` : ""}
                    </p>
                </div>
            </div>

            {hw.description && (
                <div className="card" style={{ marginBottom: "1rem" }}>
                    <div className="card-body" style={{ whiteSpace: "pre-wrap", fontSize: 13.5 }}>
                        {hw.description}
                    </div>
                </div>
            )}

            {hw.attachments?.length > 0 && (
                <div className="card" style={{ marginBottom: "1rem" }}>
                    <div className="card-header">Reference Material</div>
                    <div className="card-body">
                        {hw.attachments.map((a, i) => (
                            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                                <a href={a.url} target="_blank" rel="noreferrer">{a.fileName}</a>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {isGraded && hw.submission && (
                <div className="card" style={{ marginBottom: "1rem", borderLeft: "4px solid #15803d" }}>
                    <div className="card-body">
                        <div style={{ fontWeight: 800, fontSize: 20, color: "#15803d" }}>
                            {hw.submission.marksObtained}{hw.maxMarks ? `/${hw.maxMarks}` : ""}
                        </div>
                        {hw.submission.feedback && (
                            <div style={{ fontSize: 13, marginTop: 6 }}>
                                <strong>Feedback:</strong> {hw.submission.feedback}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {isOverdue && !hw.allowLateSubmission && !isParent && (
                <div className="card">
                    <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>
                        The due date has passed and late submissions aren't allowed for this homework.
                    </div>
                </div>
            )}

            <div className="card">
                <div className="card-header">{isParent ? "Submission" : "Your Answer"}</div>
                <div className="card-body">
                    <textarea
                        className="form-control"
                        rows={6}
                        value={submittedText}
                        onChange={(e) => setSubmittedText(e.target.value)}
                        placeholder="Type your answer here…"
                        disabled={!canEdit}
                        style={{ resize: "vertical", marginBottom: "0.75rem" }}
                    />

                    {canEdit && (
                        <label className="btn btn-outline btn-sm" style={{ width: "fit-content", cursor: "pointer" }}>
                            {uploading ? "Uploading…" : "Attach File"}
                            <input type="file" multiple hidden onChange={handleFileSelect} disabled={uploading} />
                        </label>
                    )}

                    {attachments.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                            {attachments.map((a, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 4 }}>
                                    <a href={a.url} target="_blank" rel="noreferrer">{a.fileName}</a>
                                    {canEdit && (
                                        <button
                                            type="button"
                                            onClick={() => removeAttachment(i)}
                                            style={{ background: "transparent", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 12 }}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {canEdit && (
                        <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end" }}>
                            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || uploading}>
                                {saving ? "Submitting…" : hw.submission ? "Update Submission" : "Submit"}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Default exports — App.jsx imports named exports directly
export default StudentExams;